import { useAuth0 } from '@auth0/auth0-react';
import { DevTool } from '@hookform/devtools';
import {
  Checkbox,
  ErrorMessage,
  Field,
  Input,
  Label,
  Typography,
} from '@remarkable/ark-web';
import { useQueryClient } from '@tanstack/react-query';
import { getRouteApi } from '@tanstack/react-router';
import type { DefaultValues } from 'react-hook-form';
import { Controller, useForm } from 'react-hook-form';

import { queryKeys, useCountrySuspense } from 'src/api/queries';
import { useEnterpriseEnrollmentStatus } from 'src/api/queries/enterpriseEnrollment';
import {
  Button,
  Divider,
  Feedback,
  Form,
  Sheet,
  SplitHorizontalLayout,
} from 'src/components';
import { AppPage } from 'src/components/AppPage';
import { URLS } from 'src/utils/urls/urls';

import { type ChangeEvent } from 'react';
import { ComponentLocations } from 'src/analytics/trackingTypes';
import type { BillingAddress } from 'src/api/endpoints/cloudEnterpriseEnrollmentApi.types';
import { useEnterpriseEnrollmentErrorApiInit } from 'src/api/queries/enterpriseEnrollmentErrors';
import { CountryPicker2 } from 'src/components/CountryPicker2';
import { LinkExternal } from 'src/components/LinkExternal';
import { StatePicker2 } from 'src/components/StatePicker2';
import type { SetupEnrollmentFormData } from 'src/routes/_auth/enterprise/link';
import { TestId } from 'src/testids';
import { SUPPORT_URL } from 'src/utils/urls/supportUrls';
import { userHelpers } from 'src/utils/userHelpers';
import { CancelSetupModal } from '../components/CancelSetup';
import { EnterpriseEnrollmentInitErrorNotification } from '../components/EnterpriseEnrollmentInitErrorNotification';
import { EnterpriseSteps } from '../components/EnterpriseSteps';

interface FormData {
  name: string;
  dunsNumber: string;
  taxId: string;
  tos: boolean;
  billingAddress: BillingAddress;
  company?: null;
  orderId: string;
}

function mapDefaultValuesToFormData(
  defaultValues: SetupEnrollmentFormData
): FormData {
  return {
    name: defaultValues.companyName || '',
    dunsNumber: defaultValues.duns || '',
    taxId: defaultValues.taxId || '',
    tos: false,
    billingAddress: {
      country: defaultValues.billingAddress?.country || '',
      street: defaultValues.billingAddress?.street || '',
      city: defaultValues.billingAddress?.city || '',
      postalCode: defaultValues.billingAddress?.postalCode || '',
      state: defaultValues.billingAddress?.state || '',
    },
    company: null,
    orderId: defaultValues.orderId || '',
  };
}

const routeApi = getRouteApi('/_auth/enterprise/enroll/create-organization');

export function CreateOrganizationPage() {
  // Suspense queries ---------------------------------------------------------
  const defaultCountry = useCountrySuspense();
  const enrollmentStatus = useEnterpriseEnrollmentStatus();

  // Regular queries ----------------------------------------------------------
  const navigate = routeApi.useNavigate();
  const { user } = useAuth0();
  const createEnterpriseEnrollment = useEnterpriseEnrollmentErrorApiInit();
  const emailInfo = user ? userHelpers.getEmailParts(user) : { domain: '' };
  const queryClient = useQueryClient();

  const search = routeApi.useSearch();

  const getDefaultValues = (): DefaultValues<FormData> => {
    if (enrollmentStatus.data) {
      return {
        name: enrollmentStatus.data.displayName,
        dunsNumber: enrollmentStatus.data.duns,
        taxId: enrollmentStatus.data.taxId,
        company: null,
        billingAddress: enrollmentStatus.data.billingAddress,
        tos: enrollmentStatus.data.tos,
        orderId: enrollmentStatus.data.orderId,
      };
    }

    if (search.defaultValues) {
      return mapDefaultValuesToFormData(search.defaultValues);
    }

    return {
      billingAddress: {
        country: defaultCountry.data.value,
      },
    };
  };

  const form = useForm<FormData>({
    defaultValues: getDefaultValues(),
  });

  const country = form.watch('billingAddress.country');

  const hasInitializedEnrollment = !!enrollmentStatus.data;

  const onSubmit = async (data: FormData) => {
    if (enrollmentStatus.isSuccess && !!enrollmentStatus.data) {
      // Enrollment already initiated
      return navigate({ to: '../verify-domain' });
    }

    if (typeof user?.email !== 'string') {
      throw new Error('Invalid email');
    }

    createEnterpriseEnrollment.mutate(
      {
        body: {
          displayName: data.name,
          companyName: data.name,
          domain: emailInfo.domain,
          billingEmail: user.email,
          billingAddress: data.billingAddress,
          tos: data.tos,
          duns: data.dunsNumber,
          taxId: data.taxId,
          orderId: data.orderId,
        },
      },
      {
        onSuccess: async () => {
          await queryClient.invalidateQueries({
            queryKey: queryKeys.enterpriseEnrollment.status,
          });
          return navigate({ to: '../verify-domain' });
        },
      }
    );
  };

  return (
    <AppPage.Container className="max-w-l2xl">
      <AppPage.Header
        title="Setting up Enterprise SSO"
        className="max-w-l2xl text-center"
      />
      <AppPage.Content className="max-w-ll">
        <EnterpriseSteps />
        <Sheet className="gap-16">
          <div className="flex items-center justify-between gap-16">
            <Typography as="h2" variant="heading-sm">
              Register your organization
            </Typography>
          </div>

          <Typography variant="body-md">
            This step creates your organization&apos;s account. Your
            organization&apos;s name will be visible on your dashboard, making
            it easy to identify.
          </Typography>

          <Divider />

          <DevTool control={form.control} />
          <Form
            dataCy={TestId.FormCreateOrganization}
            {...form}
            onSubmit={onSubmit}
            className="flex flex-col gap-24"
          >
            <input
              data-cy="dunsNumber"
              type="hidden"
              {...form.register('dunsNumber')}
            />
            <input
              data-cy="orderId"
              type="hidden"
              {...form.register('orderId')}
            />

            <Controller
              control={form.control}
              name="billingAddress.country"
              rules={{
                required: 'Please select a country',
                onChange(event: { target: { value: string } }) {
                  form.reset((prev) => ({
                    ...prev,
                    company: null,
                    billingAddress: {
                      ...prev.billingAddress,
                      country: event.target.value,
                    },
                  }));
                },
              }}
              disabled={hasInitializedEnrollment}
              render={({ field, fieldState }) => {
                return (
                  <Field className="w-full">
                    <Label htmlFor={field.name}>Country</Label>

                    <CountryPicker2
                      id={field.name}
                      hasError={!!fieldState.error}
                      aria-invalid={!!fieldState.error}
                      aria-errormessage={fieldState.error?.message}
                      {...field}
                    />

                    {fieldState.error?.message && (
                      <ErrorMessage data-cy="country-error-message">
                        {fieldState.error.message}
                      </ErrorMessage>
                    )}
                  </Field>
                );
              }}
            />

            <>
              <Controller
                control={form.control}
                name="name"
                rules={{
                  required: 'Organization name is required',
                }}
                disabled={hasInitializedEnrollment}
                render={({ field, fieldState }) => {
                  return (
                    <Field className="w-full">
                      <Label htmlFor={field.name}>Display name</Label>
                      <Input
                        id={field.name}
                        {...field}
                        hasError={!!fieldState.error}
                        aria-invalid={!!fieldState.error}
                        aria-errormessage={fieldState.error?.message}
                      />
                      {fieldState.error?.message && (
                        <ErrorMessage data-cy="name-error-message">
                          {fieldState.error.message}
                        </ErrorMessage>
                      )}
                    </Field>
                  );
                }}
              />

              <Controller
                control={form.control}
                name="billingAddress.street"
                rules={{
                  required: 'Street address is required',
                }}
                disabled={hasInitializedEnrollment}
                render={({ field, fieldState }) => {
                  return (
                    <Field className="w-full">
                      <Label htmlFor={field.name}>Street address</Label>
                      <Input
                        id={field.name}
                        {...field}
                        hasError={!!fieldState.error}
                        aria-invalid={!!fieldState.error}
                        aria-errormessage={fieldState.error?.message}
                      />
                      {fieldState.error?.message && (
                        <ErrorMessage data-cy="name-error-message">
                          {fieldState.error.message}
                        </ErrorMessage>
                      )}
                    </Field>
                  );
                }}
              />

              <SplitHorizontalLayout>
                <Controller
                  control={form.control}
                  name="billingAddress.postalCode"
                  rules={{
                    required: 'Postal code is required',
                  }}
                  disabled={hasInitializedEnrollment}
                  render={({ field, fieldState }) => {
                    return (
                      <Field className="w-full">
                        <Label htmlFor={field.name}>Postal code</Label>
                        <Input
                          id={field.name}
                          {...field}
                          value={field.value || ''}
                          hasError={!!fieldState.error}
                          aria-invalid={!!fieldState.error}
                          aria-errormessage={fieldState.error?.message}
                        />
                        {fieldState.error?.message && (
                          <ErrorMessage data-cy="name-error-message">
                            {fieldState.error.message}
                          </ErrorMessage>
                        )}
                      </Field>
                    );
                  }}
                />

                <Controller
                  control={form.control}
                  name="billingAddress.city"
                  rules={{
                    required: 'City is required',
                  }}
                  disabled={hasInitializedEnrollment}
                  render={({ field, fieldState }) => {
                    return (
                      <Field className="w-full">
                        <Label htmlFor={field.name}>City</Label>
                        <Input
                          id={field.name}
                          {...field}
                          value={field.value || ''}
                          hasError={!!fieldState.error}
                          aria-invalid={!!fieldState.error}
                          aria-errormessage={fieldState.error?.message}
                        />
                        {fieldState.error?.message && (
                          <ErrorMessage data-cy="name-error-message">
                            {fieldState.error.message}
                          </ErrorMessage>
                        )}
                      </Field>
                    );
                  }}
                />
              </SplitHorizontalLayout>

              <SplitHorizontalLayout>
                {country === 'US' || country === 'CA' ? (
                  <Controller
                    control={form.control}
                    name="billingAddress.state"
                    rules={{
                      required: 'State is required',
                      deps: ['billingAddress.country'],
                    }}
                    disabled={hasInitializedEnrollment}
                    render={({ field, fieldState }) => {
                      return (
                        <Field className="w-full">
                          <Label htmlFor={field.name}>State</Label>
                          <StatePicker2
                            country={country}
                            id={field.name}
                            name={field.name}
                            value={field.value}
                            onChange={(e) => {
                              field.onChange(e.currentTarget.value);
                            }}
                            disabled={field.disabled}
                            onBlur={field.onBlur}
                            hasError={!!fieldState.error}
                            aria-invalid={!!fieldState.error}
                            aria-errormessage={fieldState.error?.message}
                          />
                          {fieldState.error?.message && (
                            <ErrorMessage data-cy="name-error-message">
                              {fieldState.error.message}
                            </ErrorMessage>
                          )}
                        </Field>
                      );
                    }}
                  />
                ) : null}

                <Controller
                  control={form.control}
                  name="taxId"
                  disabled={hasInitializedEnrollment}
                  render={({ field, fieldState }) => {
                    return (
                      <Field className="w-full">
                        <Label htmlFor={field.name}>Tax ID (optional)</Label>
                        <Input
                          id={field.name}
                          {...field}
                          value={field.value || ''}
                          hasError={!!fieldState.error}
                          aria-invalid={!!fieldState.error}
                          aria-errormessage={fieldState.error?.message}
                        />
                        {fieldState.error?.message && (
                          <ErrorMessage data-cy="name-error-message">
                            {fieldState.error.message}
                          </ErrorMessage>
                        )}
                      </Field>
                    );
                  }}
                />
              </SplitHorizontalLayout>

              <Controller
                control={form.control}
                name="tos"
                rules={{
                  required: 'You must accept the terms of service',
                }}
                disabled={hasInitializedEnrollment}
                render={({ field, fieldState }) => {
                  return (
                    <Label
                      data-cy="tos"
                      htmlFor={field.name}
                      className="flex cursor-pointer items-center gap-8"
                    >
                      <Checkbox
                        id={field.name}
                        aria-invalid={!!fieldState.error}
                        aria-errormessage={fieldState.error?.message}
                        aria-label="Terms and conditions"
                        aria-description="By subscribing you accept the Terms and Conditions for Connect for Business."
                        onChange={(e: ChangeEvent<HTMLInputElement>) =>
                          field.onChange(e.currentTarget.checked)
                        }
                        onBlur={field.onBlur}
                        required
                        disabled={field.disabled}
                        checked={!!field.value}
                      ></Checkbox>
                      <span>
                        By subscribing you accept the{' '}
                        <LinkExternal
                          inline
                          to={SUPPORT_URL.ENTERPRISE_TERMS_AND_CONDITIONS}
                          componentLocation={
                            ComponentLocations.ENTERPRISE.ENROLL_ORGANIZATION
                          }
                        >
                          Terms and Conditions
                        </LinkExternal>{' '}
                        for Connect for Business.
                      </span>
                    </Label>
                  );
                }}
              />
            </>

            <div className="flex w-full flex-col-reverse flex-wrap-reverse gap-24 lm:flex-row">
              <div className="flex flex-1 ls:justify-center lm:justify-start">
                <CancelSetupModal
                  componentLocation={
                    ComponentLocations.ENTERPRISE.ENROLL_ORGANIZATION
                  }
                  trigger={
                    <Button
                      variant="tertiary-neutral"
                      className="w-full ls:w-fit"
                      analytics={{
                        location:
                          ComponentLocations.ENTERPRISE.ENROLL_ORGANIZATION,
                        text: 'Cancel',
                        action: 'cancel',
                      }}
                    >
                      Cancel
                    </Button>
                  }
                />
              </div>

              <div className="flex flex-col-reverse flex-wrap-reverse gap-24 ls:flex-row">
                <Button
                  as="a"
                  variant="secondary"
                  to={URLS.ENTERPRISE_SETUP}
                  className="w-full ls:w-fit ls:flex-1 lm:flex-initial"
                  analytics={{
                    location: ComponentLocations.ENTERPRISE.ENROLL_ORGANIZATION,
                    text: 'Previous',
                    action: 'previous',
                  }}
                >
                  Previous
                </Button>
                <Button
                  type="submit"
                  variant="primary"
                  loading={createEnterpriseEnrollment.isPending}
                  disabled={createEnterpriseEnrollment.isPending}
                  className="w-full ls:w-fit ls:flex-1 lm:flex-initial"
                  analytics={{
                    location: ComponentLocations.ENTERPRISE.ENROLL_ORGANIZATION,
                    text: 'Continue',
                    action: 'continue',
                  }}
                >
                  Continue
                </Button>
              </div>
            </div>
          </Form>
          <EnterpriseEnrollmentInitErrorNotification
            error={createEnterpriseEnrollment.error}
          />
        </Sheet>
        <Feedback
          className="mt-48"
          feedbackTargetId="enterprise-enroll-organization"
          title="Did you find what you were looking for?"
        />
      </AppPage.Content>
    </AppPage.Container>
  );
}
